import { Injectable, inject } from "@angular/core";
import { GlobalStateStore } from "@services/global-state/global-state.service";
import { AccountType } from "../../types/AccountType";
import { getClientIdOrUuid } from "@models/estateProfile.model";
import { mappedAccountTypesMap } from "../../models/account_type_mapping.model";
import * as accountDetailsContent from "@services/content/guided-flow/account-details-content";
import { WebLogger } from "@weps/shared-http";

export interface EditableItem {
  accountOwner?: string;
  accountOwnerUuid?: string;
  ownerId?: string;
  accountName?: string;
  assetName?: string;
  valuation?: number;
  balance?: number;
  mortgageAmount?: number;
  liabilityId?: string;
  realEstateType?: string;
  subAccountType?: string;
  jointType?: string;
  // Trust-specific fields
  trustType?: string;
  trustLegalName?: string;
  trustDisplayName?: string;
  trustSettlors?: any[];
  selectedAccountOwnerType?: string;
  // Institution typeahead fields
  financialInstitutionId?: string;
  financialInstitutionName?: string;
}

export interface OwnerSelectorHandlers {
  ownerSelectorDropdownChangeHandler: (event: { activeItems: string[] }) => void;
  nameInputChangeHandler: (input: string) => void;
  balanceInputChangeHandler: (input: number) => void;
  mortgageInputChangeHandler?: (input: number) => void;
  subAccountDropdownChangeHandler?: (event: { activeItems: string[] }) => void;
  // Trust-specific handlers
  trustLegalNameInputChangeHandler?: (input: string) => void;
  trustDisplayNameInputChangeHandler?: (input: string) => void;
  trustTypeDropdownChangeHandler?: (event: { activeItems: string[] }) => void;
  trustSettlorDropdownChangeHandler?: (event: { activeItems: string[] }) => void;
  // Institution typeahead handler
  institutionChangeHandler?: (id: string, name?: string) => void;
}

export interface EditFlowContext {
  accountType: AccountType;
  showSubAccountType?: boolean;
  currentAccountOwner?: string;
  currentSubAccountType?: string;
}

@Injectable({
  providedIn: "root",
})
export class EditFlowService {
  private readonly globalStore = inject(GlobalStateStore);
  protected readonly accountDetailsContent = accountDetailsContent;

  /**
   * Sets edit fields for both accounts and assets using a unified approach
   */
  setEditFields(
    context: EditFlowContext,
    handlers: OwnerSelectorHandlers,
    ownerCleared: boolean = false,
  ): { currentAccountOwner?: string; currentSubAccountType?: string } {
    const currentItem = this.globalStore.getGuidedFlowState(context.accountType);
    const estateProfile = this.globalStore.estateProfile?.();

    // Debug logging for trust accounts
    if (context.accountType === AccountType.TRUST) {
      WebLogger.log({
        message: "EditFlowService Trust debugging",
        payload: {
          accountType: context.accountType,
          hasTrustType: !!(currentItem as any)?.trustType,
          trustType: (currentItem as any)?.trustType,
          hasTrustSettlors: !!(currentItem as any)?.trustSettlors,
          selectedAccountOwnerType: (currentItem as any)?.selectedAccountOwnerType,
        },
      });
    }

    if (!estateProfile) {
      return {};
    }

    const primaryClient = estateProfile.primaryClient;
    const spouses = this.globalStore.findAllSpouseData();

    // Handle owner selection
    const ownerResult = this.handleOwnerSelection(
      currentItem,
      primaryClient,
      spouses,
      handlers.ownerSelectorDropdownChangeHandler,
      ownerCleared,
    );

    // Handle inventory item fields (name, balance, mortgage, sub-type)
    const currentSubAccountType = this.handleInventoryItemFields(currentItem, context, handlers);

    return {
      ...ownerResult,
      currentSubAccountType,
    };
  }

  /**
   * Handles owner selection logic for both accounts and assets
   */
  // TODO: Refactor to reduce cyclomatic complexity (currently 25, max 10)
  // eslint-disable-next-line complexity
  private handleOwnerSelection(
    currentItem: EditableItem,
    primaryClient: any,
    spouses: any[],
    ownerSelectorHandler: (event: { activeItems: string[] }) => void,
    ownerWasIntentionallyCleared: boolean = false,
  ): { currentAccountOwner?: string } {
    let currentAccountOwner: string | undefined;

    // Check if this is an account (uses accountOwner field), asset (uses ownerId field), VG trust (uses currentAccountOwner field), or ME Trust (uses trustType field)
    const isAccount = "accountOwner" in currentItem && currentItem.accountOwner;
    const isAsset = "ownerId" in currentItem && currentItem.ownerId;
    // Not good conditions to identify trusts, but given the lack of better indicators, we use these for now
    const isVGTrust = "currentAccountOwner" in currentItem && currentItem.currentAccountOwner;
    const isMETrust = "trustType" in currentItem;

    // Handle trust accounts FIRST (they can also have accountOwner field)
    if (isVGTrust) {
      // Handle trust owner selection - pass through the trust ID directly
      currentAccountOwner = (currentItem as any).currentAccountOwner;
    } else if (isMETrust) {
      // Handle trust owner selection - pass through the trust ID directly
      currentAccountOwner = (currentItem as any).householdMemberId;
    } else if (isAccount) {
      // Handle account owner selection
      if (
        currentItem.accountOwner === "PRIMARY" &&
        primaryClient &&
        !ownerWasIntentionallyCleared
      ) {
        ownerSelectorHandler({ activeItems: [primaryClient.piClientId || primaryClient.uuid] });
        currentAccountOwner = getClientIdOrUuid(primaryClient);
      } else if (currentItem.accountOwner === "SECONDARY" && currentItem.accountOwnerUuid) {
        // Only prefill if we have a valid accountOwnerUuid - do not fall back to first spouse
        const matchedSpouse = spouses.find(
          (s) =>
            s.uuid === currentItem.accountOwnerUuid ||
            s.piClientId === currentItem.accountOwnerUuid,
        );
        if (matchedSpouse) {
          ownerSelectorHandler({ activeItems: [matchedSpouse.piClientId || matchedSpouse.uuid] });
          currentAccountOwner = getClientIdOrUuid(matchedSpouse);
        }
      } else if (currentItem.accountOwner === "JOINT_OWNER") {
        const jointOwnershipTypeId =
          Object.values(accountDetailsContent.JOINT_OWNERSHIP_TYPES).find(
            (type) => type.value === currentItem.jointType,
          )?.id || "";
        ownerSelectorHandler({ activeItems: [jointOwnershipTypeId] });
        currentAccountOwner = jointOwnershipTypeId;
      }
    } else if (isAsset) {
      // Handle asset owner selection
      if (currentItem.jointType) {
        const jointOwnershipTypeId =
          Object.values(accountDetailsContent.JOINT_OWNERSHIP_TYPES).find(
            (type) => type.value === currentItem.jointType,
          )?.id || "";
        ownerSelectorHandler({ activeItems: [jointOwnershipTypeId] });
        currentAccountOwner = jointOwnershipTypeId;
      } else if (primaryClient && currentItem.ownerId === primaryClient.uuid) {
        ownerSelectorHandler({ activeItems: [primaryClient.piClientId || primaryClient.uuid] });
        currentAccountOwner = getClientIdOrUuid(primaryClient);
      } else {
        const matchedSpouse = spouses.find((s) => currentItem.ownerId === s.uuid);
        if (matchedSpouse) {
          ownerSelectorHandler({ activeItems: [matchedSpouse.piClientId || matchedSpouse.uuid] });
          currentAccountOwner = getClientIdOrUuid(matchedSpouse);
        }
      }
    }

    return { currentAccountOwner };
  }

  /**
   * Handles inventory item fields that are common between accounts and assets
   */
  // TODO: Refactor to reduce cyclomatic complexity (currently 19, max 10)
  // eslint-disable-next-line complexity
  private handleInventoryItemFields(
    currentItem: EditableItem,
    context: EditFlowContext,
    handlers: OwnerSelectorHandlers,
  ): string | undefined {
    // Handle trust accounts specially with dedicated handlers
    if (context.accountType === AccountType.TRUST) {
      const trustLegalName = currentItem.trustLegalName;
      const trustDisplayName = currentItem.trustDisplayName;
      const trustType = currentItem.trustType;

      // Use trust-specific handlers if available
      if (trustLegalName && handlers.trustLegalNameInputChangeHandler) {
        handlers.trustLegalNameInputChangeHandler(trustLegalName);
      }

      if (trustDisplayName && handlers.trustDisplayNameInputChangeHandler) {
        handlers.trustDisplayNameInputChangeHandler(trustDisplayName);
      }

      if (trustType && handlers.trustTypeDropdownChangeHandler) {
        // Only set trust type if it's valid (REVOCABLE or IRREVOCABLE)
        // If trustType is "UNKNOWN", leave it empty so user must select
        const trustTypeUpper = trustType.toUpperCase();
        if (trustTypeUpper === "REVOCABLE" || trustTypeUpper === "IRREVOCABLE") {
          handlers.trustTypeDropdownChangeHandler({
            activeItems: [trustTypeUpper],
          });
        } else {
          WebLogger.log({
            message: "EditFlowService Invalid trustType",
            level: "warn",
            payload: { trustType, expected: "REVOCABLE or IRREVOCABLE" },
          });
        }
      }
      return this.handleSubAccountType(currentItem, context, handlers);
    }

    // Handle regular accounts and assets
    const itemName =
      context.accountType === AccountType.REAL_ESTATE
        ? currentItem.assetName || currentItem.accountName
        : currentItem.accountName || currentItem.assetName;
    if (itemName) {
      handlers.nameInputChangeHandler(itemName);
    }

    // Pass institution fields through for institution-eligible account types
    if (handlers.institutionChangeHandler && currentItem.financialInstitutionId) {
      handlers.institutionChangeHandler(
        currentItem.financialInstitutionId,
        currentItem.financialInstitutionName,
      );
    }

    // Handle balance/valuation input
    const itemValue = currentItem.valuation || currentItem.balance;
    if (itemValue) {
      handlers.balanceInputChangeHandler(itemValue);
    }

    // Handle mortgage amount for liabilities
    if (
      currentItem.liabilityId &&
      currentItem.mortgageAmount &&
      handlers.mortgageInputChangeHandler
    ) {
      handlers.mortgageInputChangeHandler(currentItem.mortgageAmount);
    }

    // Handle sub-account type dropdown and return the current sub-account type
    return this.handleSubAccountType(currentItem, context, handlers);
  }

  /**
   * Handles sub-account type dropdown logic
   */
  private handleSubAccountType(
    currentItem: EditableItem,
    context: EditFlowContext,
    handlers: OwnerSelectorHandlers,
  ): string | undefined {
    if (!handlers.subAccountDropdownChangeHandler) {
      return undefined;
    }

    if (context.accountType === AccountType.TRUST) {
      const trustType = (currentItem as any).trustType;

      if (trustType) {
        const trustTypeId = trustType.toUpperCase();

        handlers.subAccountDropdownChangeHandler({
          activeItems: [trustTypeId],
        });

        return trustTypeId;
      }

      return undefined;
    }

    const subAccountTypeToCheck = currentItem.realEstateType || currentItem.subAccountType;

    if (!subAccountTypeToCheck) {
      return undefined;
    }

    // Handle bank accounts specially (no sub-account types)
    if (context.accountType === AccountType.BANK) {
      return undefined;
    }

    // Check if the sub-account type exists in the mapping
    if (mappedAccountTypesMap[context.accountType]?.[subAccountTypeToCheck]) {
      const currentSubAccountType = currentItem.realEstateType || currentItem.subAccountType;

      handlers.subAccountDropdownChangeHandler({
        activeItems: [mappedAccountTypesMap[context.accountType][subAccountTypeToCheck]],
      });

      return currentSubAccountType;
    }

    return undefined;
  }

  /**
   * Determines if an item is an asset based on its properties
   */
  isAsset(item: EditableItem): boolean {
    return "assetName" in item || "ownerId" in item;
  }

  /**
   * Determines if an item is an account based on its properties
   */
  isAccount(item: EditableItem): boolean {
    return "accountOwner" in item && item.accountOwner !== undefined;
  }

  /**
   * Gets the appropriate name field for the item
   */
  getItemName(item: EditableItem): string | undefined {
    return item.accountName || item.assetName;
  }

  /**
   * Gets the appropriate value field for the item
   */
  getItemValue(item: EditableItem): number | undefined {
    return item.valuation || item.balance;
  }

  /**
   * Gets the appropriate sub-type field for the item
   */
  getItemSubType(item: EditableItem): string | undefined {
    return item.realEstateType || item.subAccountType;
  }
}
